﻿<script setup lang="ts">
    import { ref } from 'vue';
    import { type FontReference } from '@shared/components/FontTypes';
    import { type TypeDesignSettings, uis } from '@shared/components/DesignSettings/defaultSettings';
    import DesignSettings from '@shared/components/DesignSettings/DesignSettings.vue';
    import { Action } from '@shared/components/RichEditor/Types.d';
    import { SwitchStyle } from '@shared/components/Form/Form';

    const { slider, colorPicker, toggle, fontSelector } = uis;

    const props = defineProps<{
        cssVars: { [key: string]: string };
        googleFonts: string;
    }>();

    const availableFonts = ref<FontReference[]>([]);

    const emit = defineEmits(['update:cssVars', 'update:googleFonts']);

    const designSettings = (): TypeDesignSettings => {
        return {
            Frame: {
                groupName: $Resources.FormEditorDesignHeadingFrame,
                settings: {
                    Width: slider({ label: $Resources.FormEditorDesignWidth, options: { min: 400, max: 800, step: 10 } }),
                    BackgroundColor: colorPicker({ label: $Resources.FormEditorDesignBackgroundColor }),
                    PaddingTop: slider({
                        label: $Resources.FormEditorDesignPaddingTop,
                        sectionHeading: $Resources.EditorPaddingHeading,
                        options: { max: 40, cols: 6 },
                    }),
                    PaddingRight: slider({ label: $Resources.FormEditorDesignPaddingRight, options: { max: 40, cols: 6 } }),
                    PaddingBottom: slider({ label: $Resources.FormEditorDesignPaddingBottom, options: { max: 40, cols: 6 } }),
                    PaddingLeft: slider({ label: $Resources.FormEditorDesignPaddingLeft, options: { max: 40, cols: 6 } }),
                    Radius: slider({
                        label: $Resources.FormEditorDesignRadius,
                        sectionHeading: $Resources.ThemeEditorBorderLabel,
                        options: { max: 40, cols: 4 },
                    }),
                    BorderWidth: slider({ label: $Resources.FormEditorDesignBorderWidth, options: { max: 6, cols: 4 } }),
                    BorderColor: colorPicker({ label: $Resources.FormEditorDesignBorderColor, options: { cols: 4 } }),
                },
            },
            Image: {
                groupName: $Resources.FormEditorDesignHeadingImage,
                settings: {
                    FlexDirection: toggle({
                        heading: $Resources.FormEditorDesignImageLayoutHeading,
                        value: 'row',
                        toggleOptions: [
                            { value: 'column', label: $Resources.FormEditorDesignImageLayoutTop },
                            { value: 'row', label: $Resources.FormEditorDesignImageLayoutLeft },
                            { value: 'row-reverse', label: $Resources.FormEditorDesignImageLayoutRight },
                        ],
                        options: {
                            cols: 'auto',
                        },
                    }),
                    Width: slider({
                        label: $Resources.FormEditorDesignWidth,
                        options: { min: 10, max: 60, step: 5, unit: '%' },
                    }),
                },
            },
            Heading: {
                groupName: $Resources.FormEditorDesignHeadingHeading,
                settings: {
                    FontFamily: fontSelector({ label: $Resources.FormEditorDesignFontFamily }),
                    FontSize: slider({ label: $Resources.FormEditorDesignFontSize, options: { min: 12, max: 48, cols: 4 } }),
                    LineHeight: slider({ label: $Resources.FormEditorDesignLineHeight, options: { min: 90, max: 200, unit: '%', cols: 4 } }),
                    Color: colorPicker({ label: $Resources.FormEditorDesignColor, options: { cols: 4 } }),
                    FontWeight: toggle({
                        label: $Resources.FormEditorDesignFontWeight,
                        value: 'normal',
                        toggleOptions: [{ value: 'bold', label: $Resources.BoldLabel, icon: Action.bold, uncheckedValue: 'normal' }],
                        options: { cols: 'auto', switchStyle: SwitchStyle.Button },
                    }),
                },
            },
            Text: {
                groupName: $Resources.FormEditorDesignHeadingText,
                settings: {
                    FontFamily: fontSelector({ label: $Resources.FormEditorDesignFontFamily }),
                    FontSize: slider({ label: $Resources.FormEditorDesignFontSize, options: { min: 12, max: 24, cols: 4 } }),
                    LineHeight: slider({ label: $Resources.FormEditorDesignLineHeight, options: { min: 90, max: 200, unit: '%', cols: 4 } }),
                    Color: colorPicker({ label: $Resources.FormEditorDesignColor, options: { cols: 4 } }),
                    FontWeight: toggle({
                        label: $Resources.FormEditorDesignFontWeight,
                        value: 'normal',
                        toggleOptions: [{ value: 'bold', label: $Resources.BoldLabel, icon: Action.bold, uncheckedValue: 'normal' }],
                        options: { cols: 'auto', switchStyle: SwitchStyle.Button },
                    }),
                },
            },
            Labels: {
                groupName: $Resources.FormEditorDesignHeadingLabels,
                settings: {
                    FontFamily: fontSelector({ label: $Resources.FormEditorDesignFontFamily }),
                    FontSize: slider({ label: $Resources.FormEditorDesignFontSize, options: { min: 12, max: 24, cols: 4 } }),
                    LineHeight: slider({ label: $Resources.FormEditorDesignLineHeight, options: { min: 90, max: 200, unit: '%', cols: 4 } }),
                    Color: colorPicker({ label: $Resources.FormEditorDesignColor, options: { cols: 4 } }),
                    FontWeight: toggle({
                        label: $Resources.FormEditorDesignFontWeight,
                        value: 'bold',
                        toggleOptions: [{ value: 'bold', label: $Resources.BoldLabel, icon: Action.bold, uncheckedValue: 'normal' }],
                        options: { cols: 'auto', switchStyle: SwitchStyle.Button },
                    }),
                },
            },
            Inputs: {
                groupName: $Resources.FormEditorDesignHeadingInputs,
                settings: {
                    BackgroundColor: colorPicker({ label: $Resources.FormEditorDesignBackgroundColor }),
                    PaddingTop: slider({
                        label: $Resources.FormEditorDesignPaddingTop,
                        sectionHeading: $Resources.EditorPaddingHeading,
                        options: { max: 40, cols: 6 },
                    }),
                    PaddingRight: slider({ label: $Resources.FormEditorDesignPaddingRight, options: { max: 40, cols: 6 } }),
                    PaddingBottom: slider({ label: $Resources.FormEditorDesignPaddingBottom, options: { max: 40, cols: 6 } }),
                    PaddingLeft: slider({ label: $Resources.FormEditorDesignPaddingLeft, options: { max: 40, cols: 6 } }),
                    BorderTopWidth: slider({
                        label: $Resources.FormEditorDesignBorderTopWidth,
                        sectionHeading: $Resources.ThemeEditorBorderLabel,
                        options: { max: 6, cols: 6 },
                    }),
                    BorderRightWidth: slider({ label: $Resources.FormEditorDesignBorderRightWidth, options: { max: 6, cols: 6 } }),
                    BorderBottomWidth: slider({ label: $Resources.FormEditorDesignBorderBottomWidth, options: { max: 6, cols: 6 } }),
                    BorderLeftWidth: slider({ label: $Resources.FormEditorDesignBorderLeftWidth, options: { max: 6, cols: 6 } }),
                    Radius: slider({ label: $Resources.FormEditorDesignRadius, options: { max: 20, cols: 6 } }),
                    BorderColor: colorPicker({ label: $Resources.FormEditorDesignBorderColor, options: { cols: 6 } }),
                    FontFamily: fontSelector({ label: $Resources.FormEditorDesignFontFamily, sectionHeading: $Resources.FormEditorDesignHeadingText }),
                    FontSize: slider({ label: $Resources.FormEditorDesignFontSize, options: { min: 12, max: 24, cols: 4 } }),
                    LineHeight: slider({ label: $Resources.FormEditorDesignLineHeight, options: { min: 90, max: 200, unit: '%', cols: 4 } }),
                    Color: colorPicker({ label: $Resources.FormEditorDesignColor, options: { cols: 4 } }),
                },
            },
            Button: {
                groupName: $Resources.FormEditorDesignHeadingButton,
                settings: {
                    BackgroundColor: colorPicker({ label: $Resources.FormEditorDesignBackgroundColor, options: { cols: 6 } }),
                    Color: colorPicker({ label: $Resources.FormEditorDesignColor, options: { cols: 6 } }),
                    PaddingTop: slider({
                        label: $Resources.FormEditorDesignPaddingTop,
                        sectionHeading: $Resources.EditorPaddingHeading,
                        options: { max: 40, cols: 6 },
                    }),
                    PaddingRight: slider({ label: $Resources.FormEditorDesignPaddingRight, options: { max: 40, cols: 6 } }),
                    PaddingBottom: slider({ label: $Resources.FormEditorDesignPaddingBottom, options: { max: 40, cols: 6 } }),
                    PaddingLeft: slider({ label: $Resources.FormEditorDesignPaddingLeft, options: { max: 40, cols: 6 } }),
                    Width: toggle({
                        label: $Resources.FormEditorDesignWidth,
                        sectionHeading: $Resources.SizeAndPositionHeading,
                        value: 'auto',
                        toggleOptions: [{ value: '100%', label: $Resources.FormEditorDesignWidthFull, uncheckedValue: 'auto' }],
                        options: {
                            cols: 6,
                            switchStyle: SwitchStyle.Standard,
                        },
                    }),
                    JustifyContent: toggle({
                        label: $Resources.FormEditorDesignJustifyContent,
                        value: 'start',
                        toggleOptions: [
                            { value: 'start', label: $Resources.FormEditorDesignAlignmentLeft, icon: Action.justifyStart },
                            { value: 'center', label: $Resources.FormEditorDesignAlignmentCenter, icon: Action.justifyCenter },
                            { value: 'end', label: $Resources.FormEditorDesignAlignmentRight, icon: Action.justifyEnd },
                        ],
                        options: { cols: 6, switchStyle: SwitchStyle.Button },
                    }),
                    BorderWidth: slider({
                        label: $Resources.FormEditorDesignBorderWidth,
                        sectionHeading: $Resources.ThemeEditorBorderLabel,
                        options: { max: 6, cols: 4 },
                    }),
                    Radius: slider({ label: $Resources.FormEditorDesignRadius, options: { max: 40, cols: 4 } }),
                    BorderColor: colorPicker({ label: $Resources.FormEditorDesignBorderColor, options: { cols: 4 } }),
                    FontFamily: fontSelector({ label: $Resources.FormEditorDesignFontFamily, sectionHeading: $Resources.FormEditorDesignHeadingText }),
                    FontSize: slider({ label: $Resources.FormEditorDesignFontSize, options: { min: 12, max: 28, cols: 4 } }),
                    LineHeight: slider({ label: $Resources.FormEditorDesignLineHeight, options: { min: 90, max: 200, unit: '%', cols: 4 } }),
                    FontWeight: toggle({
                        label: $Resources.FormEditorDesignFontWeight,
                        value: 'normal',
                        toggleOptions: [{ value: 'bold', label: $Resources.BoldLabel, icon: Action.bold, uncheckedValue: 'normal' }],
                        options: { cols: 4, switchStyle: SwitchStyle.Button },
                    }),
                },
            },
        };
    };
</script>

<script lang="ts"></script>

<template>
    <DesignSettings
        :cssVars="props.cssVars"
        :googleFonts="props.googleFonts"
        :designSettings="designSettings()"
        :availableFonts="availableFonts"
        @update:cssVars="(name, newValue) => $emit('update:cssVars', name, newValue)"
        @update:googleFonts="(newValue) => $emit('update:googleFonts', newValue)"
        @update:availableFonts="(newValue) => (availableFonts = newValue)"></DesignSettings>
</template>

<style scoped></style>

<style lang="less"></style>
